import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Clock, Layers, Zap, User } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function loadRzp() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

const normPhone = (p) => { const d = String(p || "").replace(/\D/g, ""); return d.length > 10 ? d.slice(-10) : d; };

export default function ProgramDetail() {
  const { slug } = useParams();
  const { user, persistAuth } = useAuth();
  const navigate = useNavigate();
  const [prog, setProg] = useState(null);
  const [level, setLevel] = useState("");
  const [lang, setLang] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [showDetails, setShowDetails] = useState(false);
  const [busy, setBusy] = useState(false);
  const tracked = useRef({});

  const track = (event) => {
    if (tracked.current[event]) return;
    tracked.current[event] = true;
    api.post("/events", { event, program_id: prog?.id }).catch(() => {});
  };

  useEffect(() => { api.get(`/programs/${slug}`).then((r) => setProg(r.data)).catch(() => {}); }, [slug]);
  useEffect(() => { if (prog) track("challenge_viewed"); }, [prog]); // eslint-disable-line
  // English-only (or any single-option) programs: preselect so there's one less tap
  useEffect(() => {
    if (!prog) return;
    if ((prog.languages || []).length === 1) setLang(prog.languages[0]);
    if ((prog.levels || []).length === 1) setLevel(prog.levels[0]);
  }, [prog]);
  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || f.name, email: user.email || f.email, phone: (user.profile || {}).phone || f.phone }));
  }, [user]);

  if (!prog) return <div className="pt-32 text-center text-muted-foreground">Loading…</div>;
  const priceINR = ((prog.price_paise || 0) / 100).toLocaleString("en-IN");

  const pay = async () => {
    if (!form.name.trim()) { toast.error("Enter your full name."); return; }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) { toast.error("Enter a valid email."); return; }
    const phone = normPhone(form.phone);
    if (phone.length !== 10) { toast.error("Enter a valid 10-digit mobile number."); return; }
    setBusy(true);
    const ok = await loadRzp();
    if (!ok) { toast.error("Could not load Razorpay. Try again."); setBusy(false); return; }
    try {
      let data, headers = {};
      const payload = { program_id: prog.id, level, language: lang, name: form.name.trim(), email: form.email.trim(), phone };
      if (user) {
        ({ data } = await api.post("/payments/order", payload));
      } else {
        ({ data } = await api.post("/payments/guest-order", payload));
        headers = { Authorization: `Bearer ${data.checkout_token}` };
        track("registration_completed");
      }
      track("checkout_opened");
      const rzp = new window.Razorpay({
        key: data.key_id, amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: data.name, description: data.description, prefill: data.prefill, theme: { color: "#FF5722" },
        handler: async (r) => {
          try {
            const v = await api.post("/payments/verify", r, { headers });
            track("payment_success");
            if (v.data.access_token) persistAuth({ token: v.data.access_token, user: v.data.user });
            navigate(`/payment/success?order=${data.internal_order_id}&enrolment=${v.data.enrolment_id}${v.data.login_required ? "&login=1" : ""}`);
          } catch (e) { navigate(`/payment/failed?order=${data.internal_order_id}`); }
        },
        modal: { ondismiss: () => navigate(`/payment/pending?order=${data.internal_order_id}`) },
      });
      rzp.on("payment.failed", () => navigate(`/payment/failed?order=${data.internal_order_id}`));
      rzp.open();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const enrol = () => {
    track("enrol_clicked");
    if (!level || !lang) { toast.error("Pick your level and language first."); return; }
    if (user) { pay(); return; }
    setShowDetails(true);
    setTimeout(() => document.getElementById("guest-details")?.scrollIntoView({ behavior: "smooth", block: "center" }), 60);
  };

  const btn = (val, cur) => `px-4 py-3 rounded-lg border text-sm font-bold transition-colors ${cur === val ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`;

  return (
    <div data-testid="program-detail" className="pb-16">
      <PageHeader overline="Paid Program" title={prog.title} subtitle={prog.tagline} />
      <section className="mx-auto max-w-3xl px-5 py-10 md:py-12">
        {prog.thumbnail && (
          <img src={prog.thumbnail} alt={prog.title} data-testid="program-artwork"
            className="w-full aspect-video object-cover rounded-xl border border-border mb-8" />
        )}
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-center text-xs mb-8">
          <div className="border border-border rounded-lg p-3 sm:p-4"><Clock className="mx-auto text-primary mb-2" size={20} /><div className="font-bold">{prog.duration_days} Days</div><div className="text-[10px] text-muted-foreground mt-0.5">25 Min/Day</div></div>
          <div className="border border-border rounded-lg p-3 sm:p-4"><Zap className="mx-auto text-primary mb-2" size={20} /><div className="font-bold">No Equipment</div><div className="text-[10px] text-muted-foreground mt-0.5">Daily unlock</div></div>
          <div className="border border-border rounded-lg p-3 sm:p-4"><Shield className="mx-auto text-primary mb-2" size={20} /><div className="font-bold">Secure</div><div className="text-[10px] text-muted-foreground mt-0.5">Razorpay</div></div>
        </div>
        <p className="text-muted-foreground mb-8">{prog.description}</p>

        <div className="border border-border rounded-xl bg-card p-5 sm:p-6 space-y-6">
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">1 · Select fitness level</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {(prog.levels || []).map((l) => <button key={l} data-testid={`level-${l}`} onClick={() => setLevel(l)} className={btn(l, level)}>{l}</button>)}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">2 · Select language</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {(prog.languages || []).map((l) => <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className={btn(l, lang)}>{l}</button>)}
            </div>
          </div>

          <div className="border-t border-border pt-4 flex items-baseline justify-between" data-testid="checkout-summary">
            <div>
              <div className="text-xs uppercase text-muted-foreground">{prog.title}</div>
              <div className="font-display font-900 text-4xl text-primary">₹{priceINR}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Start for just ₹{priceINR} — one time</div>
            </div>
            <div className="text-xs text-muted-foreground text-right">Level: <b>{level || "—"}</b><br />Language: <b>{lang || "—"}</b></div>
          </div>

          {user && (
            <div className="flex items-center gap-2.5 text-xs text-muted-foreground bg-secondary/60 rounded-lg px-4 py-3" data-testid="continue-as">
              <User size={14} className="text-primary shrink-0" /> Continuing as <b className="text-foreground">{user.name || user.email}</b>
            </div>
          )}

          <button onClick={enrol} disabled={busy} data-testid="join-challenge"
            className="w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">
            {busy ? "Opening secure checkout…" : `Enrol Now — ₹${priceINR}`}
          </button>

          {showDetails && !user && (
            <div id="guest-details" data-testid="guest-details-form" className="border border-primary/40 rounded-xl p-5 space-y-4 bg-background">
              <div className="text-center">
                <h3 className="font-display font-800 uppercase text-2xl tracking-tight">Almost there!</h3>
                <p className="text-xs text-muted-foreground mt-1">Enter your details to continue to secure payment.</p>
              </div>
              <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Full Name</Label><Input data-testid="pay-name" className="mt-1.5 h-12" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" /></div>
              <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Mobile Number</Label><Input data-testid="pay-phone" inputMode="tel" className="mt-1.5 h-12" value={form.phone} onChange={(e) => { setForm({ ...form, phone: e.target.value }); track("registration_started"); }} placeholder="10-digit mobile" autoComplete="tel" /></div>
              <div><Label className="text-xs uppercase tracking-[0.12em] text-muted-foreground">Email Address</Label><Input data-testid="pay-email" type="email" className="mt-1.5 h-12" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" /></div>
              <button onClick={pay} disabled={busy} data-testid="continue-to-payment"
                className="w-full bg-primary text-primary-foreground py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.02] transition-transform disabled:opacity-60">
                {busy ? "Opening secure checkout…" : `Continue to Payment — ₹${priceINR}`}
              </button>
              <p className="text-[11px] text-center text-muted-foreground">No password needed now — your account is created automatically after payment.</p>
            </div>
          )}

          <p className="text-[11px] text-center text-muted-foreground flex items-center gap-1.5 justify-center"><Shield size={12} /> Secure payment via Razorpay · UPI · Cards · Net Banking</p>
        </div>
      </section>
    </div>
  );
}
